import { useState } from 'react';
import {
  AritoFormV2,
  AritoIcon,
  BulkDeleteDialogV4,
  DeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { ActionBar, GeneralInfoTab, HddtTab, HeaderTab, OtherTab, Sidebar } from './components';
import { initialKhachHangValues, KhachHangFormValues, khachHangSchema } from './schemas';
import type { DataTableRowEventParams } from '@/components/custom/erp/data-table-v2';
import { KhachHangProvider, useKhachHangContext } from './context';
import type { KhachHang, KhachHangInput } from '@/types/schemas';
import { ExcelConfig, ImportBody } from './utils/excel-util';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { exportMainColumns } from './cols-definition';
import { useGeneralInfoState } from './hooks';

const PAGE_SIZE = 100;

function KhachHangPageContent() {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const {
    tables,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    addItem: addKhachHang,
    updateItem: updateKhachHang,
    deleteItem: deleteKhachHang,
    refreshData: refreshKhachHangs,
    handleSearchChange,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    showBulkDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleCloseBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleOpenImportExcel
  } = useKhachHangContext();

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const showDeleteDialog = showDelete;
  const setShowDeleteDialog = (value: boolean) => {
    if (value) {
      handleDeleteClick();
    } else {
      handleCloseDelete();
    }
  };
  const handleOpenAddForm = handleAddClick;
  const handleOpenEditForm = handleEditClick;
  const showSidebar = false;
  const toggleSidebar = () => {};

  const generalInfoState = useGeneralInfoState(formMode, selectedObj || undefined);

  const handleFormSubmit = async (data: KhachHangFormValues) => {
    try {
      const updatedData = {
        ...data,
        is_customer: true,
        is_vendor: false,
        customer_group1: generalInfoState.nhomKH1?.uuid,
        customer_group2: generalInfoState.nhomKH2?.uuid,
        customer_group3: generalInfoState.nhomKH3?.uuid,
        region: generalInfoState.khuVuc?.uuid,
        account: generalInfoState.taiKhoan?.uuid,
        payment_term: generalInfoState.hanThanhToan?.uuid,
        payment_method: generalInfoState.phuongThucThanhToan?.uuid,
        ma_nvbh: generalInfoState.nhanVien?.uuid,
        sales_rep: generalInfoState.nhanVien?.uuid,
        credit_limit: data.credit_limit || 0
      };

      if (formMode === 'add') {
        await addKhachHang(updatedData);
        handleCloseForm();
        clearSelection();
        await refreshKhachHangs();
      } else if (formMode === 'edit' && selectedObj) {
        await updateKhachHang(selectedObj.uuid, updatedData);
        handleCloseForm();
        clearSelection();
        await refreshKhachHangs();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addKhachHang(body as KhachHangInput);
      }
      await refreshKhachHangs();
      toast({
        description: 'Đã nhập danh mục khách hàng từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  return (
    <div className='flex h-screen w-full min-w-0 max-w-full flex-col overflow-hidden'>
      <div className={`${showForm ? 'hidden' : 'flex h-full overflow-hidden'}`}>
        <Sidebar showSidebar={showSidebar} toggleSidebar={toggleSidebar} />

        <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
          <div className='flex shrink-0 items-center border-b bg-white'>
            <div className='flex w-full min-w-0'>
              <ActionBar
                onAddClick={handleOpenAddForm}
                onEditClick={() => handleOpenEditForm()}
                onDeleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                onCopyClick={() => handleCopyClick()}
                onRefreshClick={() => refreshKhachHangs()}
                onShowSidebar={toggleSidebar}
                onImportFromExcel={handleOpenImportExcel}
              />
            </div>
          </div>

          {/* DataTable container */}
          <div data-excel-operation-overlay-root className='min-w-0 flex-1 overflow-auto'>
            <ErpDataTableV2
              tables={tables}
              onRowClick={params => handleRowClick(params as DataTableRowEventParams<KhachHang>)}
              onRowDoubleClick={params => handleViewClick(params.row as KhachHang)}
              selectedRowId={selectedRowIndex || undefined}
              getRowId={row => row.uuid}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={PAGE_SIZE}
              onSearchChange={handleSearchChange}
              loading={isLoading}
              widthMode='stretch'
              className='h-full'
            />
          </div>
        </div>
      </div>

      {showForm && (
        <AritoFormV2
          mode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? (selectedObj as unknown as KhachHangFormValues)
              : formMode === 'add' && !isCopyMode
                ? initialKhachHangValues
                : undefined
          }
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
          title={formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Xem'}
          subTitle='Danh mục khách hàng'
          schema={khachHangSchema}
          headerFields={<HeaderTab mode={formMode} />}
          tabs={[
            { id: 'basic_info', label: 'Thông tin chung', component: <GeneralInfoTab {...generalInfoState} /> },
            { id: 'other', label: 'Khác', component: <OtherTab /> },
            { id: 'hddt', label: 'HDDT', component: <HddtTab mode={formMode} /> }
          ]}
        />
      )}

      {showDeleteDialog && (
        <DeleteDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          selectedObj={selectedObj}
          deleteObj={deleteKhachHang}
          clearSelection={clearSelection}
        />
      )}
      {showBulkDelete && (
        <BulkDeleteDialogV4
          title='Khách hàng'
          onClose={handleCloseBulkDelete}
          columns={exportMainColumns(handleViewClick)}
          dataRows={tables[0].rows}
          handleDelete={deleteKhachHang}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}

export default function KhachHangPage() {
  return (
    <KhachHangProvider>
      <KhachHangPageContent />
    </KhachHangProvider>
  );
}
